import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Col, Container, Row, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { CommissionPercentSettings, getAdminSettings, updateStaffCommission } from '../../../apis/queries';
import Loader from '../../../components/Loader';
import { getInitials } from '../../../helpers/getInitials';
import { User } from '../../../types';
import CommissionSettings from './CommissionSettings';
import FinancialMonth from './FinancialMonth';
import NotificationEmail from './NotificationEmail';

// Mirror the API's defaults (Khufu src/helpers/commissions.ts), shown until a tenant sets its own.
const DEFAULT_DRIVER_COMMISSION_PERCENT = 20;
const DEFAULT_MOTORBOY_COMMISSION_PERCENT = 10;

interface RootState {
  user: {
    currentUser: User | null;
  };
}

const AdminSettings = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');

  const userDetails = useSelector((state: RootState) => state.user.currentUser);
  const initials = getInitials(userDetails?.fullName);

  const { data: adminSettingsData, isLoading: isSettingsLoading } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: () => getAdminSettings()
  });

  const mutation = useMutation({
    mutationFn: (data: Required<CommissionPercentSettings>) => updateStaffCommission(data),
    onSuccess: () => {
      handleShowToast('Commissions updated successfully!', 'success');
    },
    onError: () => {
      handleShowToast('Failed to update commissions. Please try again.', 'danger');
    }
  });

  const handleSaveCommission = async (driverCommissionPercent: number, motorBoyCommissionPercent: number) => {
    await mutation.mutateAsync({ driverCommissionPercent, motorBoyCommissionPercent });
  };

  const handleShowToast = (message: string, variant: 'success' | 'danger') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  if (isSettingsLoading) {
    return <Loader />;
  }

  return (
    <div className="main-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={8}>
            <form>
              <Row className="justify-content-center align-items-center">
                <Col>
                  <Row className="justify-content-center align-items-center">
                    <Col xs="auto">
                      <div className="avatar avatar-sm avatar-online">
                        <span className="avatar-title rounded-circle bg-secondary">{initials}</span>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <hr className="my-5" />
              {/* Notification Email */}
              <NotificationEmail
                onShowToast={handleShowToast}
                initialEmails={adminSettingsData?.notificationEmails || []}
              />
              {/* Commission Rates */}
              <CommissionSettings
                onSaveCommission={handleSaveCommission}
                initialDriverCommission={
                  adminSettingsData?.driverCommissionPercent ?? DEFAULT_DRIVER_COMMISSION_PERCENT
                }
                initialMotorBoyCommission={
                  adminSettingsData?.motorBoyCommissionPercent ?? DEFAULT_MOTORBOY_COMMISSION_PERCENT
                }
              />
              {/* Financial Month */}
              <FinancialMonth onShowToast={handleShowToast} />
            </form>
          </Col>
        </Row>

        <ToastContainer position="top-end" className="p-3">
          <Toast show={showToast} delay={5000} autohide onClose={() => setShowToast(false)} bg={toastVariant}>
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      </Container>
    </div>
  );
};

export default AdminSettings;
